import { Button, Modal, ModalHeaderIconVariation, ModalProps } from "@oysterhr/ui"

import { OysterIcon } from "@oysterhr/icons"
import { isFunction } from "lodash-es"
import { useState, MouseEvent } from "react"

import { useTranslation } from "react-i18next"

type ExcludedProps = "onRequestClose" | "isOpen"
export interface ConfirmModalProps extends Omit<ModalProps, ExcludedProps> {
  visible: boolean
  onClose: () => void
  onOk?: (() => void) | ((event: MouseEvent<HTMLButtonElement>) => Promise<unknown>)
  cancelLabel?: string
  okLabel?: string
  title: string
  dataTestId?: string
  variant?: "danger" | "primary"
  icon?: OysterIcon
  iconVariation?: ModalHeaderIconVariation
}

export function ConfirmModal({
  onClose,
  onOk,
  visible,
  title,
  children,
  okLabel,
  cancelLabel,
  dataTestId,
  variant,
  icon,
  iconVariation,
  ...props
}: React.PropsWithChildren<ConfirmModalProps>) {
  const [waitingForResponse, setWaitingForResponse] = useState(false)
  const { t } = useTranslation("components/ConfirmModal")

  return (
    <Modal data-testid={dataTestId} isOpen={visible} onRequestClose={onClose} {...props}>
      <Modal.Header icon={icon} align={icon ? "center" : undefined} iconVariation={iconVariation}>
        {title}
      </Modal.Header>
      <Modal.Content>{children}</Modal.Content>
      <Modal.Footer>
        <Button onClick={onClose} variation="outline">
          {cancelLabel || t("cancel")}
        </Button>
        {onOk && (
          <Button
            data-testid="modal-primary-button"
            variation={variant}
            onClick={async (ev: MouseEvent<HTMLButtonElement>) => {
              const response = onOk(ev)
              if (!isFunction(response?.then)) {
                return
              }

              setWaitingForResponse(true)
              try {
                await response
              } finally {
                setWaitingForResponse(false)
              }
            }}
            disabled={waitingForResponse}
            isLoading={waitingForResponse}
          >
            {okLabel || t("confirm")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
